import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cross2Icon,
  DesktopIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  IconButton,
  Skeleton,
  Text,
} from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { VoidIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { ConnectLiveboardDialog } from 'components/common/liveboard/connect-liveboard-dialog';
import { CommonMenu } from 'components/common/menu';
import { HitRecapDiamond } from 'components/common/pitch-recap/diamond-view';
import { PitchRecapPlate } from 'components/common/pitch-recap/plate-view';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import env from 'config';
import { AimingContext } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import useLiveboard from 'hooks/useLiveboard';
import { t } from 'i18next';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import { gaussianRandom } from 'lib_ts/classes/math.utilities';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { PitchType } from 'lib_ts/enums/pitches.enums';
import { PlateOutcome } from 'lib_ts/enums/plate.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import {
  EMPTY_HIT_DETAILS,
  EMPTY_PITCH_DETAILS,
  ICommonPitchRecap,
  IRecapStrikezone,
} from 'lib_ts/interfaces/pitches/i-common-pitch-recap';
import { cloneDeep, find, isNil } from 'lodash';
import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SessionEventsService } from 'services/session-events.service';
import { useAuthStore } from 'stores/use-auth-store';
import useHittersStore from 'stores/use-hitters-store';
import { v4 } from 'uuid';
import './index.scss';

const MAX_PITCH_DATA_WAIT_MS = 20_000;
const MAX_HIT_DATA_WAIT_MS = 30_000;
const MAX_ILLUSTRATION_WIDTH_PX = '164px';

const getVLaunchIcon = (value?: number | null) => {
  if (isNil(value)) {
    return <VoidIcon />;
  }

  if (value < 0) {
    return <ArrowDownIcon />;
  }

  return <ArrowUpIcon />;
};

const getHLaunchIcon = (value?: number | null) => {
  if (isNil(value)) {
    return <VoidIcon />;
  }

  if (value < 0) {
    // towards 3B
    return <ArrowLeftIcon />;
  }

  // towards 1B
  return <ArrowRightIcon />;
};

const MetricBox = (props: {
  label: string;
  value: string | undefined;
  truncateValue?: boolean;
  units?: string;
  icon?: ReactNode;
  className?: string;
  placeholder?: boolean;
}) => {
  const randomWidth = useMemo(() => {
    return props.placeholder ? (30 + Math.random() * 50).toFixed(0) : '0';
  }, [props.placeholder]);

  return (
    <Flex
      direction="column"
      justify="center"
      style={{ height: '100%' }}
      className={StringHelper.classNames(['RecapBox', props.className])}
      p={{
        initial: '2',
        lg: '3',
      }}
    >
      <Text
        color={RADIX.COLOR.NEUTRAL}
        className="MetricTitle text-nowrap"
        truncate
      >
        {t(props.label)}
      </Text>

      {props.placeholder && (
        <Skeleton
          className="MetricPlaceholder"
          style={{
            width: `${randomWidth}%`,
          }}
        >
          Lorem ipsum
        </Skeleton>
      )}

      {!props.placeholder && (
        <Flex gap="1" align="baseline">
          <Text
            weight="bold"
            color={props.value ? undefined : RADIX.COLOR.NEUTRAL}
            className="MetricValue text-nowrap"
            truncate={props.truncateValue}
          >
            {props.value ?? '-'}
          </Text>

          {props.units && (
            <Text color={RADIX.COLOR.NEUTRAL} className="MetricUnits">
              {props.units}
            </Text>
          )}

          <Box flexGrow="1">{/* placeholder */}</Box>

          <Text color={RADIX.COLOR.NEUTRAL}>{props.icon ?? <VoidIcon />}</Text>
        </Flex>
      )}
    </Flex>
  );
};

interface IUserPitchRecapExt extends ICommonPitchRecap {
  id: string;
  placeholder?: boolean;

  // show loading state for hit data until we receive our second pitch recap for this fire
  awaitingHitData?: boolean;
}

const EMPTY_RECAP_STRIKEZONE: IRecapStrikezone = {
  outcome: PlateOutcome.NoData,
  grid: 'empty',
  grid_x: -1,
  grid_z: -1,
  szDef: null,
  plateFt: null,
};

export const PitchRecap = (props: { paused?: boolean }) => {
  const activeHitter = useHittersStore((s) => s.activeHitter);

  const { pitchBP } = useContext(AimingContext);
  const { machine, activeStrikeZone, lastFired, lastFireID } =
    useContext(MachineContext);

  const [lastMount] = useState(Date.now());
  const [recaps, setRecaps] = useState<IUserPitchRecapExt[]>([]);
  const [index, setIndex] = useState(0);

  const {
    connectLiveboardDialog,
    openConnectLiveboardDialog,
    closeConnectLiveboardDialog,
    openLiveboard,
  } = useLiveboard();

  const recapTimeouts = useRef<{
    [fireID: string]: {
      pitchTimeout?: ReturnType<typeof setTimeout>; // how long to show loading state for pitch data
      hitTimeout?: ReturnType<typeof setTimeout>; // how long to show loading state for hit data
    };
  }>({});

  const isInGame = useAuthStore((s) => s.isInGame);

  // when mounting, load up the recent recaps (if any) as a starting point
  useEffect(() => {
    SessionEventsService.getRecentRecaps().then((result) => {
      if (result) {
        const recentRecaps = result.map((m) => ({
          ...m,
          placeholder: false,
          awaitingHitData: false,
          id: v4(),
        }));

        setRecaps(recentRecaps);
        setIndex(recentRecaps.length - 1);
      }
    });
  }, []);

  useEffect(() => {
    if (!isInGame) {
      return;
    }

    usePitchListStore.setState({ showRecap: false });
  }, [isInGame, machine.tracking_device]);

  useEffect(() => {
    if (machine.tracking_device === TrackingDevice.TrajektVision) {
      return;
    }

    usePitchListStore.setState({ showRecap: false });
  }, [machine.tracking_device]);

  // start a timer in case no recap data arrives to remove the placeholder animation
  useEffect(() => {
    if (props.paused) {
      // do nothing
      return;
    }

    if (!lastFireID) {
      return;
    }

    recapTimeouts.current[lastFireID] = {
      // after 20 seconds, we'll stop showing loading state for this fire's pitch if we haven't received it yet
      pitchTimeout: setTimeout(() => {
        setRecaps((prev) =>
          prev.map((r) => {
            if (r.fireID !== lastFireID) {
              return r;
            }

            if (!r.placeholder) {
              return r;
            }

            return {
              ...r,
              placeholder: false,
            };
          })
        );
      }, MAX_PITCH_DATA_WAIT_MS),

      // after 30 seconds, we'll stop showing loading state for this fire's hit if we haven't received it yet
      hitTimeout: setTimeout(() => {
        setRecaps((prev) =>
          prev.map((r) => {
            if (r.fireID !== lastFireID) {
              return r;
            }

            if (!r.awaitingHitData) {
              return r;
            }

            return {
              ...r,
              awaitingHitData: false,
            };
          })
        );
      }, MAX_HIT_DATA_WAIT_MS),
    };
  }, [lastFireID, props.paused]);

  useEffect(() => {
    if (props.paused) {
      // do nothing
      return;
    }

    if (!lastFired) {
      // do nothing at start
      return;
    }

    if (!lastFireID) {
      // do nothing at start
      return;
    }

    if (lastFired < lastMount) {
      // ignore if last fired before this component was mounted
      return;
    }

    const pitchName = pitchBP?.name;

    if (!pitchName) {
      return;
    }

    setRecaps((prev) => {
      const placeholder: IUserPitchRecapExt = {
        placeholder: true,
        awaitingHitData: true, // set to false once we receive our second recap for this fire
        projector: null,
        pitch: {
          ...EMPTY_PITCH_DETAILS,
          name: pitchName,
          strikeZone: {
            ...EMPTY_RECAP_STRIKEZONE,
            szDef: activeStrikeZone,
          },
        },
        hit: {
          ...EMPTY_HIT_DETAILS,
          hitterName: activeHitter?.name ?? null,
        },
        fireID: lastFireID,
        id: v4(),
      };

      const nextRecaps = [...prev, placeholder];
      return nextRecaps;
    });
  }, [lastFired, lastFireID, props.paused]);

  useEffect(() => {
    setIndex((prev) => {
      if (prev >= recaps.length) {
        // avoid illegal index (e.g. if a timeout happens and the recaps list shrinks by 1)
        return Math.max(0, recaps.length - 1);
      }

      return prev === recaps.length - 2 ? recaps.length - 1 : prev;
    });
  }, [recaps]);

  const activeRecap = useMemo(
    () => find(recaps, (_, i) => i === index),
    [recaps, index]
  );

  // we will not show invalid hit data
  const safeRecapHit = activeRecap?.hit?.invalid ? undefined : activeRecap?.hit;

  const clearPlaceholderTimeout = (fireID: string) => {
    const placeholderTimeout = recapTimeouts.current[fireID]?.pitchTimeout;

    if (!placeholderTimeout) {
      return;
    }

    clearTimeout(placeholderTimeout);
    delete recapTimeouts.current[fireID]?.pitchTimeout;
  };

  const clearHitTimeout = (fireID: string) => {
    const hitTimeout = recapTimeouts.current[fireID]?.hitTimeout;

    if (!hitTimeout) {
      return;
    }

    clearTimeout(hitTimeout);
    delete recapTimeouts.current[fireID]?.hitTimeout;
  };

  // push new recap to end of list
  useEffect(() => {
    if (props.paused) {
      // do nothing
      return;
    }

    // * Note - as of Jan 2026, we may receive multiple recaps for each pitch (one with pitch data, one with pitch & hit data)
    const handleRecap = (event: CustomEvent) => {
      const data = event.detail as ICommonPitchRecap;

      if (data.pitch) {
        clearPlaceholderTimeout(data.fireID);
      }

      if (data.hit?.processed) {
        clearHitTimeout(data.fireID);
      }

      setRecaps((prev) => {
        // we should always have this now
        const existingRecapIndex = prev.findIndex(
          (r) => r.fireID === data.fireID
        );

        if (existingRecapIndex === -1) {
          return prev;
        }

        return cloneDeep(prev).map((r) => {
          if (r.fireID !== data.fireID) {
            return r;
          }

          return {
            ...r,
            ...data,
            placeholder: !data.pitch,
            awaitingHitData: !data.hit?.processed,
          };
        });
      });
    };

    WebSocketHelper.on(WsMsgType.S2U_PitchRecap, handleRecap);
    return () => WebSocketHelper.remove(WsMsgType.S2U_PitchRecap, handleRecap);
  }, [activeHitter?.name, activeStrikeZone, props.paused]);

  // todo: use IPitchRecapUpdate / S2U_PitchRecapUpdate to update recaps by fireID with uploaded videos

  const { isMetric } = machine;

  const speedUnit = MetricHelper.getSpeedUnit(isMetric);
  const breakUnit = MetricHelper.getBreaksUnit(isMetric);
  const distanceUnit = MetricHelper.getDistanceUnit(isMetric);

  const speed = isMetric
    ? MetricHelper.mphToKph(activeRecap?.pitch?.speedMph)
    : activeRecap?.pitch?.speedMph;

  const breakZ = isMetric
    ? MetricHelper.inToCm(activeRecap?.pitch?.break?.zInches)
    : activeRecap?.pitch?.break?.zInches;

  const breakX = isMetric
    ? MetricHelper.inToCm(activeRecap?.pitch?.break?.xInches)
    : activeRecap?.pitch?.break?.xInches;

  const distance = isMetric
    ? MetricHelper.ftToM(safeRecapHit?.distanceFT)
    : safeRecapHit?.distanceFT;

  const exitVelo = isMetric
    ? MetricHelper.mphToKph(safeRecapHit?.exitMPH)
    : safeRecapHit?.exitMPH;

  const awaitingHitData = !!(
    activeRecap?.placeholder || activeRecap?.awaitingHitData
  );

  return (
    <ErrorBoundary identifier="PitchRecap">
      <Card className="PitchRecapContainer" style={{ flexShrink: 0 }}>
        <Flex mb="2" gap="2" justify="between" align="center">
          <Text
            className="PitchRecapHeader"
            weight="medium"
            onClick={() => {
              if (env.production && env.identifier !== 'staging') {
                return;
              }

              setRecaps((prev) => {
                const o: IUserPitchRecapExt = {
                  projector: null,
                  pitch: {
                    type: PitchType.FourSeamFastball,
                    name: `Sample Pitch ${prev.length + 1}`,
                    speedMph: gaussianRandom(80, 20),
                    break: {
                      xInches: gaussianRandom(0, 15),
                      zInches: gaussianRandom(0, 15),
                    },
                    spin: {
                      wx: gaussianRandom(2000, 500),
                      wy: gaussianRandom(2000, 500),
                      wz: gaussianRandom(2000, 500),
                    },
                    strikeZone: {
                      ...EMPTY_RECAP_STRIKEZONE,
                      szDef: activeStrikeZone,
                      plateFt: {
                        plate_x: gaussianRandom(0, 0.75),
                        plate_z: gaussianRandom(2.5, 0.75),
                      },
                    },
                    targets: null,
                  },
                  hit: {
                    ...HitHelper.generateSampleData(),
                    hitterName: activeHitter?.name ?? null,
                  },
                  fireID: '',
                  id: v4(),
                };

                return [...prev, o];
              });
            }}
          >
            Tracking Data
          </Text>

          {/* connect liveboards */}
          {machine.feature_flags?.liveboard && (
            <CommonMenu
              title={t('settings.connect-live-monitor')}
              trigger={
                <Button size="1" variant="soft" color={RADIX.COLOR.NEUTRAL}>
                  <DesktopIcon /> {t('settings.live-monitor')}
                </Button>
              }
              actions={[
                {
                  label: t('settings.open-live-monitor'),
                  onClick: openLiveboard,
                },
                {
                  label: t('settings.enter-live-monitor-code'),
                  onClick: openConnectLiveboardDialog,
                },
              ]}
              skipSort
            />
          )}

          <Box flexGrow="1"></Box>

          {recaps.length > 0 && (
            <Flex gap="2" align="center">
              {/* index controls */}
              <IconButton
                size="1"
                className="btn-floating"
                variant="soft"
                color={RADIX.COLOR.SECONDARY}
                disabled={index <= 0}
                onClick={() => setIndex(0)}
              >
                <DoubleArrowLeftIcon />
              </IconButton>
              <IconButton
                size="1"
                className="btn-floating"
                variant="soft"
                color={RADIX.COLOR.SECONDARY}
                disabled={index <= 0}
                onClick={() => setIndex((prev) => Math.max(prev - 1, 0))}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                {t('common.pitch')}{' '}
                {t('common.x-of-y', { x: index + 1, y: recaps.length })}
              </Text>
              <IconButton
                size="1"
                className="btn-floating"
                variant="soft"
                color={RADIX.COLOR.SECONDARY}
                disabled={index >= recaps.length - 1}
                onClick={() =>
                  setIndex((prev) => Math.min(prev + 1, recaps.length - 1))
                }
              >
                <ChevronRightIcon />
              </IconButton>
              <IconButton
                size="1"
                className="btn-floating"
                variant="soft"
                color={RADIX.COLOR.SECONDARY}
                disabled={index >= recaps.length - 1}
                onClick={() => setIndex(Math.max(0, recaps.length - 1))}
              >
                <DoubleArrowRightIcon />
              </IconButton>
            </Flex>
          )}

          <IconButton
            size="1"
            className="btn-floating"
            variant="soft"
            color={RADIX.COLOR.SECONDARY}
            onClick={() => usePitchListStore.setState({ showRecap: false })}
          >
            <Cross2Icon />
          </IconButton>
        </Flex>

        <Grid columns="9" gap="2">
          <Box gridColumn="span 5">
            {/* pitch recap */}
            <Card
              className="no-padding no-background"
              style={{ height: '100%' }}
            >
              <Grid columns="4" style={{ height: '100%' }}>
                {/* pitch stats */}
                <Box gridColumn="span 3">
                  <Grid columns="3" style={{ height: '100%' }}>
                    <Box gridColumn="span 3">
                      <MetricBox
                        className="BB"
                        label="common.pitch"
                        value={activeRecap?.pitch?.name}
                        truncateValue
                      />
                    </Box>

                    <MetricBox
                      className="BR"
                      label="common.speed"
                      value={speed?.toFixed(1)}
                      units={speedUnit}
                      placeholder={activeRecap?.placeholder}
                    />
                    <MetricBox
                      className="BR"
                      label="common.v-break"
                      value={breakZ?.toFixed(1)}
                      units={breakUnit}
                      icon={
                        activeRecap?.pitch?.break?.zInches === undefined ? (
                          <VoidIcon />
                        ) : activeRecap.pitch.break.zInches > 0 ? (
                          <ArrowUpIcon />
                        ) : (
                          <ArrowDownIcon />
                        )
                      }
                      placeholder={activeRecap?.placeholder}
                    />
                    <MetricBox
                      label="common.h-break"
                      value={breakX?.toFixed(1)}
                      units={breakUnit}
                      icon={
                        activeRecap?.pitch?.break?.xInches === undefined ? (
                          <VoidIcon />
                        ) : activeRecap.pitch.break.xInches > 0 ? (
                          // towards 3B
                          <ArrowLeftIcon />
                        ) : (
                          // towards 1B
                          <ArrowRightIcon />
                        )
                      }
                      placeholder={activeRecap?.placeholder}
                    />
                  </Grid>
                </Box>
                {/* pitch plate */}
                <Flex className="RecapBox BL" align="center" justify="center">
                  <Box maxWidth={MAX_ILLUSTRATION_WIDTH_PX}>
                    <PitchRecapPlate
                      key={
                        activeRecap
                          ? `plate-recap-${activeRecap.id}`
                          : 'no-plate-recap'
                      }
                      pitchDetails={activeRecap?.pitch}
                      strikeZone={
                        activeRecap?.pitch?.strikeZone?.szDef ??
                        activeStrikeZone
                      }
                      fillContainer
                    />
                  </Box>
                </Flex>
              </Grid>
            </Card>
          </Box>
          <Box gridColumn="span 4">
            {/* hit recap */}
            <Card
              className="no-padding no-background"
              style={{ height: '100%' }}
            >
              <Grid columns="3" style={{ height: '100%' }}>
                {/* hit diamond */}
                <Flex className="RecapBox BR" align="center" justify="center">
                  <Box maxWidth={MAX_ILLUSTRATION_WIDTH_PX}>
                    <HitRecapDiamond
                      hitter={activeRecap?.hit?.hitterName}
                      hitDetails={safeRecapHit}
                    />
                  </Box>
                </Flex>

                {/* hit stats */}
                <Box gridColumn="span 2">
                  <Grid
                    columns="2"
                    align="center"
                    justify="center"
                    style={{ height: '100%' }}
                  >
                    <MetricBox
                      className="BB"
                      label="common.exit-velocity"
                      value={exitVelo?.toFixed(1)}
                      units={speedUnit}
                      placeholder={awaitingHitData}
                    />
                    <MetricBox
                      className="BB BL"
                      label="common.distance"
                      value={distance?.toFixed(0)}
                      units={distanceUnit}
                      placeholder={awaitingHitData}
                    />
                    <MetricBox
                      className=""
                      label="common.launch-angle"
                      value={safeRecapHit?.vLaunchDEG?.toFixed(0)}
                      units="deg"
                      icon={getVLaunchIcon(safeRecapHit?.vLaunchDEG)}
                      placeholder={awaitingHitData}
                    />
                    <MetricBox
                      className="BL"
                      label="common.exit-direction"
                      value={safeRecapHit?.hLaunchDEG?.toFixed(0)}
                      units="deg"
                      icon={getHLaunchIcon(safeRecapHit?.hLaunchDEG)}
                      placeholder={awaitingHitData}
                    />
                  </Grid>
                </Box>
              </Grid>
            </Card>
          </Box>
        </Grid>
      </Card>
      {connectLiveboardDialog && (
        <ConnectLiveboardDialog
          key={connectLiveboardDialog}
          identifier="RecapConnectLiveboardDialog"
          onClose={closeConnectLiveboardDialog}
        />
      )}
    </ErrorBoundary>
  );
};
